/**
 * RentalStockIssueSection — "ใบเบิกอุปกรณ์ที่ผูกกับสัญญานี้" ในหน้ารายละเอียดการเช่า
 *
 * แทนที่ปุ่ม "ขออุปกรณ์เสริม" เดิม (โมดูล `accessory_requests` ถูกยุบ — PO 2026-09-07)
 * ใบเบิกที่สร้างจากที่นี่จะ prefill `rental_id` ไว้ให้ ซึ่งเป็นการ **อ้างอิงเฉย ๆ**
 * ไม่กระทบสถานะ/ยอดของสัญญา (ต่างจาก accessory_requests ที่บังคับผูกสัญญา ACTIVE)
 *
 * backend: GET /stock-issues/?rental_id=... (perm `stock_issues.view`)
 */
import React from 'react';
import { useQuery } from '@tanstack/react-query';
import { Link, useLocation } from 'react-router-dom';
import {
  RiInboxUnarchiveLine,
  RiAddLine,
  RiExternalLinkLine,
  RiErrorWarningLine,
} from 'react-icons/ri';
import StockIssueStatusBadge from './StockIssueStatusBadge';
import { detailNavState } from '../detail';
import { stockIssueDetailUrl, stockIssueNewUrl } from '../../utils/stockIssueLinks';
import stockIssueService, {
  issuedToDisplay,
  purposeLabel,
} from '../../services/stockIssueService';
import { usePermission } from '../../hooks/usePermission';
import { formatDate } from '../../utils/dateUtils';

interface RentalStockIssueSectionProps {
  rentalId: number;
  rentalNumber?: string | null;
}

/**
 * ใบเบิกเปิด/สร้างเป็นหน้าเต็ม (28 ต.ค. 2026): `/stock/issues/:id` · `/stock/issues/new?rental_id=` —
 * ปุ่มกลับของหน้านั้นพากลับมาหน้าสัญญานี้ (`from`) · `rentalNumber` ไม่ต้องส่งต่อแล้ว (หน้าสร้างโหลดเลขที่สัญญาเอง)
 */
const RentalStockIssueSection: React.FC<RentalStockIssueSectionProps> = ({
  rentalId,
}) => {
  const location = useLocation();
  const hereState = detailNavState(location);
  const { hasPermission } = usePermission();
  const canView = hasPermission('stock_issues.view');
  const canCreate = hasPermission('stock_issues.create');

  const { data, isLoading, error } = useQuery({
    queryKey: ['stock-issues', 'by-rental', rentalId],
    queryFn: () => stockIssueService.getIssues({ rental_id: rentalId, page_size: 20 }),
    // ไม่มีสิทธิ์ดู = ไม่ยิง API (กัน 403 เงียบ ๆ — pattern G-27)
    enabled: canView && !!rentalId,
    retry: false,
  });

  // ไม่มีสิทธิ์ทั้งดูและสร้าง → ไม่ต้องมีการ์ดนี้เลย
  if (!canView && !canCreate) return null;

  const issues = data?.items || [];

  return (
    <>
      <div className="bg-white rounded-lg shadow-sm border border-secondary-200">
        <div className="px-6 py-4 border-b border-secondary-200 flex items-center justify-between gap-2">
          <h2 className="text-lg font-medium text-secondary-900 flex items-center">
            <RiInboxUnarchiveLine className="w-5 h-5 mr-2 text-secondary-500" />
            ใบเบิกอุปกรณ์ที่เกี่ยวข้อง
            {issues.length > 0 && (
              <span className="ml-2 bg-secondary-100 text-secondary-600 text-sm px-2 py-0.5 rounded-full">
                {issues.length}
              </span>
            )}
          </h2>
          {canCreate && (
            <Link
              to={stockIssueNewUrl(rentalId)}
              state={hereState}
              className="inline-flex items-center px-3 min-h-[44px] sm:min-h-[32px] py-1.5 text-sm font-medium text-primary-700 bg-primary-50 rounded-md hover:bg-primary-100 transition-colors"
            >
              <RiAddLine className="w-4 h-4 mr-1" />
              เบิกอุปกรณ์
            </Link>
          )}
        </div>

        <div className="p-6">
          {!canView ? (
            <p className="text-sm text-secondary-500">
              บัญชีของคุณสร้างใบเบิกได้ แต่ไม่มีสิทธิ์ดูรายการใบเบิก
            </p>
          ) : isLoading ? (
            <div className="flex items-center justify-center py-6 text-secondary-500">
              <span className="text-sm">กำลังโหลด...</span>
            </div>
          ) : error ? (
            <div className="flex items-start gap-2 text-sm text-secondary-500">
              <RiErrorWarningLine className="w-4 h-4 text-warning-500 flex-shrink-0 mt-0.5" />
              <span>
                ไม่สามารถโหลดใบเบิกของสัญญานี้ได้ — ดูรายการทั้งหมดได้ที่{' '}
                <Link to="/stock/issues" className="text-primary-600 hover:underline">
                  เมนูคลังอุปกรณ์
                </Link>
              </span>
            </div>
          ) : issues.length === 0 ? (
            <div className="text-center py-8 text-secondary-500">
              <RiInboxUnarchiveLine className="w-12 h-12 mx-auto text-secondary-300 mb-3" />
              <p>ยังไม่มีใบเบิกที่ผูกกับสัญญานี้</p>
              {canCreate && (
                <Link
                  to={stockIssueNewUrl(rentalId)}
                  state={hereState}
                  className="mt-4 inline-flex items-center px-4 min-h-[44px] sm:min-h-[38px] py-2 text-sm font-medium text-primary-700 bg-primary-50 rounded-md hover:bg-primary-100 transition-colors"
                >
                  <RiAddLine className="w-4 h-4 mr-1" />
                  สร้างใบเบิกสำหรับสัญญานี้
                </Link>
              )}
            </div>
          ) : (
            <div className="space-y-3">
              {issues.map((issue) => (
                <Link
                  key={issue.id}
                  to={stockIssueDetailUrl(issue.id)}
                  state={hereState}
                  className="block w-full text-left border border-secondary-200 rounded-lg p-4 hover:bg-secondary-50 transition-colors"
                >
                  <div className="flex flex-wrap items-center justify-between gap-2 mb-1">
                    <span className="font-medium text-secondary-900">{issue.issue_number}</span>
                    <StockIssueStatusBadge
                      status={issue.status}
                      statusLabelText={issue.status_label}
                      isReturnable={issue.is_returnable}
                      isOverdue={issue.is_overdue}
                      daysOverdue={issue.days_overdue}
                      size="xs"
                    />
                  </div>
                  <p className="text-sm text-secondary-700">
                    {issue.purpose_label || purposeLabel(issue.purpose)}
                    {typeof issue.item_count === 'number' ? ` · ${issue.item_count} รายการ` : ''}
                  </p>
                  <p className="text-xs text-secondary-500">
                    ผู้เบิก: {issuedToDisplay(issue)}
                    {issue.issue_date ? ` · เบิก ${formatDate(issue.issue_date)}` : ''}
                  </p>
                </Link>
              ))}
              <Link
                to="/stock/issues?tab=history"
                className="inline-flex items-center text-xs text-primary-600 hover:underline"
              >
                ดูใบเบิกทั้งหมดในระบบ
                <RiExternalLinkLine className="w-3.5 h-3.5 ml-1" />
              </Link>
            </div>
          )}
        </div>
      </div>
    </>
  );
};

export default RentalStockIssueSection;
